import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { MapPinIcon } from "lucide-react";

/* ===== Leaflet additions ===== */
import "leaflet/dist/leaflet.css";
import { MapContainer, TileLayer, Marker } from "react-leaflet";
import L from "leaflet";
import iconUrl from "leaflet/dist/images/marker-icon.png";
import iconRetinaUrl from "leaflet/dist/images/marker-icon-2x.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

const pdfThumbnail = "/pdf_icon.png";
const API_BASE = process.env.REACT_APP_API_BASE || "";

// fix CRA/Vite asset paths for default marker icons
L.Icon.Default.mergeOptions({
  iconRetinaUrl,
  iconUrl,
  shadowUrl,
});
/* ===== end Leaflet additions ===== */

/* ====== HTTPS-safe URL helpers ====== */
const RAW_ORIGIN_FOR_UPLOADS =
  (process.env.REACT_APP_API_BASE && process.env.REACT_APP_API_BASE.replace(/\/+$/, "")) ||
  window.location.origin.replace(/\/+$/, "");

// Always upgrade to https when composing URLs
const ORIGIN_FOR_UPLOADS = RAW_ORIGIN_FOR_UPLOADS.startsWith("http://")
  ? RAW_ORIGIN_FOR_UPLOADS.replace("http://", "https://")
  : RAW_ORIGIN_FOR_UPLOADS;

// turn a server-side file path into an absolute, safely-encoded URL (always https)
const uploadUrlFromPath = (p) => {
  if (!p) return null;
  const s = String(p);

  // ❗️If it's S3 or already an absolute URL or data:, DO NOT rewrite to /uploads/...
  if (s.startsWith("s3://") || /^https?:\/\//i.test(s) || s.startsWith("data:")) {
    return null;
  }

  const fname = s.split(/\\|\//).pop();
  if (!fname) return null;
  const safeName = encodeURIComponent(fname);
  return `${ORIGIN_FOR_UPLOADS}/uploads/${safeName}`;
};

// ensure an image URL is fetchable (PDF-safe). If cross-origin, route via proxy. Always https.
const toFetchable = (url) => {
  if (!url) return null;
  try {
    const base = new URL(ORIGIN_FOR_UPLOADS);
    base.protocol = "https:";
    const u = new URL(url, base.href);

    if (u.protocol === "http:") u.protocol = "https:";

    const sameOrigin = u.origin === base.origin;
    if (sameOrigin) return u.href;
    return `${base.origin}/api/proxy-image?url=${encodeURIComponent(u.href)}`;
  } catch {
    return `${ORIGIN_FOR_UPLOADS}${url.startsWith("/") ? "" : "/"}${url}`;
  }
};
/* ====== end helpers ====== */

/* ====== S3 presign helpers (for s3://bucket/key paths) ====== */
const s3KeyFromPath = (p) => {
  if (!p || !p.startsWith("s3://")) return null;
  const parts = p.split("/");
  if (parts.length < 4) return null;
  return parts.slice(3).join("/");
};

const presignReadUrl = async (key) => {
  const { data } = await axios.get(`${API_BASE}/api/s3/presign-read`, {
    params: { key },
  });
  return data?.url || null;
};
/* ====== end S3 presign helpers ====== */

const Dashboard = () => {
  const [kycData, setKycData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [viewRecord, setViewRecord] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (err) {
      console.warn("Logout request failed", err);
    }
    sessionStorage.removeItem("isAuthenticated");
    navigate("/login");
  };

  // score for dedupe
  const recordScore = (r) => {
    const fields = [
      r.name, r.father, r.dob, r.mobile, r.address, r.state, r.pincode,
      r.fileUrl, r.candidatePhotoUrl, r.aadhaarPhotoFileUrl, r.aadhaarPhotoSourceUrl,
      r.latitude, r.longitude
    ];
    const base = fields.reduce((acc, v) => acc + (v ? 1 : 0), 0);
    const serialBonus = Array.isArray(r.serialNumbers) ? Math.min(3, r.serialNumbers.filter(Boolean).length) : 0;
    return base + serialBonus;
  };

  const dedupeAndFilter = (list) => {
    const byVerif = new Map();

    for (const r of list || []) {
      const verif = r?.digilocker?.verificationId || r?._id;
      const existing = byVerif.get(verif);
      if (!existing) {
        byVerif.set(verif, r);
      } else {
        const better = recordScore(r) >= recordScore(existing) ? r : existing;
        byVerif.set(verif, better);
      }
    }

    const cleaned = Array.from(byVerif.values()).filter((r) => {
      const hasAnyUserField =
        r.name || r.father || r.dob || r.mobile || r.address || r.state || r.pincode;
      const hasAnyFile =
        r.fileUrl || r.candidatePhotoUrl || r.aadhaarPhotoFileUrl || r.aadhaarPhotoSourceUrl;
      return !!(hasAnyUserField || hasAnyFile);
    });

    return cleaned;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/api/kyc-data`);
      const cleaned = dedupeAndFilter(res.data || []);

      // Replace s3:// paths with presigned URLs for fileUrl-like fields
      const withPresigned = await Promise.all(
        cleaned.map(async (r) => {
          const copy = { ...r };

          if (copy.filePath && String(copy.filePath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.filePath);
            copy.fileUrl = key ? await presignReadUrl(key) : null;
          }

          if (copy.candidatePhotoPath && String(copy.candidatePhotoPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.candidatePhotoPath);
            copy.candidatePhotoUrl = key ? await presignReadUrl(key) : null;
          }

          if (copy.aadhaarPhotoPath && String(copy.aadhaarPhotoPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.aadhaarPhotoPath);
            copy.aadhaarPhotoFileUrl = key ? await presignReadUrl(key) : null;
          }

          // NEW: trainingCertificate & extraDocument
          if (copy.trainingCertificatePath && String(copy.trainingCertificatePath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.trainingCertificatePath);
            copy.trainingCertificateUrl = key ? await presignReadUrl(key) : null;
          } else if (copy.trainingCertificatePath) {
            // local path serving from /uploads
            copy.trainingCertificateUrl = uploadUrlFromPath(copy.trainingCertificatePath) || null;
          }

          if (copy.extraDocumentPath && String(copy.extraDocumentPath).startsWith("s3://")) {
            const key = s3KeyFromPath(copy.extraDocumentPath);
            copy.extraDocumentUrl = key ? await presignReadUrl(key) : null;
          } else if (copy.extraDocumentPath) {
            copy.extraDocumentUrl = uploadUrlFromPath(copy.extraDocumentPath) || null;
          }

          // ensure old-style fileUrl fields (if already present) are preserved
          return copy;
        })
      );

      setKycData(withPresigned);
      setFilteredData(withPresigned);
    } catch (err) {
      console.error("Error fetching data", err);
      setError("Failed to load records.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    let data = [...kycData];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      data = data.filter((item) => {
        const serials = Array.isArray(item.serialNumbers) ? item.serialNumbers.join(" ").toLowerCase() : "";
        return (
          (item.name || "").toLowerCase().includes(lower) ||
          (item.mobile || "").toLowerCase().includes(lower) ||
          (getDigiMaskedUid(item) || "").toLowerCase().includes(lower) ||
          serials.includes(lower)
        );
      });
    }
    if (startDate || endDate) {
      const s = startDate ? new Date(startDate) : null;
      const e = endDate ? new Date(endDate) : null;
      data = data.filter((item) => {
        const d = item.createdAt ? new Date(item.createdAt) : null;
        if (!d || isNaN(d)) return false;
        return (!s || d >= s) && (!e || d <= e);
      });
    }
    setFilteredData(data);
  }, [searchTerm, startDate, endDate, kycData]);

  const formatDate = (d) => {
    if (!d) return "-";
    const date = new Date(d);
    return isNaN(date) ? d : date.toLocaleDateString();
  };

  // DIGILOCKER HELPERS
  const getDigi = (rec) => rec?.digilocker || {};
  const getDigiStatus = (rec) => getDigi(rec)?.status || "-";
  const getDigiVerificationId = (rec) => getDigi(rec)?.verificationId || "-";
  const getDigiMaskedUid = (rec) =>
    getDigi(rec)?.document?.uid_masked ||
    getDigi(rec)?.userDetails?.uid_masked ||
    rec?.aadhaar || "-";

  // IMAGE HELPERS — prefer *Path (rebuilt https) then sanitized absolute
  const getCandidatePhotoUrl = (rec) => {
    const p = rec.candidatePhotoPath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.candidatePhotoUrl ? toFetchable(rec.candidatePhotoUrl) : null);
  };
  const getAadhaarImageUrl = (rec) => {
    const p = rec.filePath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.fileUrl ? toFetchable(rec.fileUrl) : null);
  };

  const getTrainingCertificateUrl = (rec) => {
    const p = rec.trainingCertificatePath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.trainingCertificateUrl ? toFetchable(rec.trainingCertificateUrl) : null);
  };
  const getExtraDocumentUrl = (rec) => {
    const p = rec.extraDocumentPath;
    const local = p && !String(p).startsWith("s3://") ? uploadUrlFromPath(p) : null;
    return local || (rec.extraDocumentUrl ? toFetchable(rec.extraDocumentUrl) : null);
  };

  const getAadhaarPortraitUrl = (rec) => {
    const src = rec?.aadhaarPhotoFileUrl
      ? rec.aadhaarPhotoFileUrl
      : rec?.aadhaarPhotoSourceUrl || null;

    if (!src) return null;
    if (/^data:/i.test(src)) return src;
    if (!/^https?:\/\//i.test(src) && /^[A-Za-z0-9+/=]+$/.test(src.slice(0, 64))) {
      return `data:image/jpeg;base64,${src}`;
    }
    return toFetchable(src);
  };

  const isImageFile = (url = "") => {
    if (!url) return false;
    if (/^data:image\//i.test(url)) return true;
    const u = url.split("?")[0].toLowerCase();
    return /\.(jpe?g|png|gif|webp|bmp)$/i.test(u);
  };

  const isPdfFile = (url = "") => {
    if (!url) return false;
    const u = url.split("?")[0].toLowerCase();
    return /\.pdf$/i.test(u) || u.includes("application/pdf");
  };

  // EXPORT CSV (include carryArea and training/extradoc)
  const exportCSV = () => {
    const headers = [
      "Name","Mobile","Aadhaar(masked)","DOB","Father","Address","Pincode","State",
      "Latitude","Longitude","SerialNumbers","DL_Status","DL_VerificationId","DL_UID(masked)",
      "PurposeDetails","CarryArea","Convicted","AppliedBefore","LicenseSuspended","FamilyHasLicence","SafePlaceToKeep","TrainingUnderRule10",
      "TrainingCertificate","AdditionalDocument"
    ];
    const rows = filteredData.map((r) => {
      const op = r.otherParticulars || {};
      const trainUrl = getTrainingCertificateUrl(r) || r.trainingCertificateUrl || "";
      const extraUrl = getExtraDocumentUrl(r) || r.extraDocumentUrl || "";
      const trainName = trainUrl ? (trainUrl.split("/").pop() || trainUrl) : "";
      const extraName = extraUrl ? (extraUrl.split("/").pop() || extraUrl) : "";
      return [
        cleanCsv(r.name),
        cleanCsv(r.mobile),
        cleanCsv(r.aadhaar),
        cleanCsv(formatDate(r.dob)),
        cleanCsv(r.father),
        cleanCsv(r.address),
        cleanCsv(r.pincode),
        cleanCsv(r.state),
        cleanCsv(r.latitude),
        cleanCsv(r.longitude),
        cleanCsv(Array.isArray(r.serialNumbers) ? r.serialNumbers.join(" | ") : ""),
        cleanCsv(getDigiStatus(r)),
        cleanCsv(getDigiVerificationId(r)),
        cleanCsv(getDigiMaskedUid(r)),
        cleanCsv(op.purposeDetails || ""),
        cleanCsv(op.carryArea || ""),
        cleanCsv(op.convicted || ""),
        cleanCsv(op.appliedBefore || ""),
        cleanCsv(op.licenseSuspended || ""),
        cleanCsv(op.familyHasLicence || ""),
        cleanCsv(op.safePlaceToKeep || ""),
        cleanCsv(op.trainingUnderRule10 || ""),
        cleanCsv(trainName),
        cleanCsv(extraName)
      ].join(",");
    });
    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, "kyc_data.csv");
  };

  const cleanCsv = (v) => {
    if (v == null) return "";
    const str = String(v).replace(/"/g, '""');
    return str.includes(",") || str.includes("\n") ? `"${str}"` : str;
  };

  // EXPORT PDF (list)
  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("KYC Records", 14, 20);

    const tableColumn = [
      "Name","Mobile","Aadhaar(masked)","DOB","Father","Serial No(s)","Address","Pincode","State","DL Status"
    ];

    const tableRows = filteredData.map((r) => [
      r.name || "-",
      r.mobile || "-",
      getDigiMaskedUid(r),
      formatDate(r.dob),
      r.father || "-",
      (Array.isArray(r.serialNumbers) && r.serialNumbers.length) ? r.serialNumbers.join(", ") : "-",
      r.address || "-",
      r.pincode || "-",
      r.state || "-",
      getDigiStatus(r)
    ]);

    autoTable(doc, {
      startY: 30,
      head: [tableColumn],
      body: tableRows,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [100, 100, 255] },
    });

    doc.save("kyc_data.pdf");
  };

  // fetch image as blob -> PNG data URL
  const urlToDataURL = async (url) => {
    if (/^data:image\//i.test(url)) {
      const fmt = /data:image\/png/i.test(url) ? "PNG" : "JPEG";
      return { dataUrl: url, format: fmt };
    }

    const safeUrl = toFetchable(url);

    const buildUrl = (u) => {
      const abs = new URL(u, ORIGIN_FOR_UPLOADS);
      abs.protocol = "https:";
      abs.searchParams.set("_cb", Date.now().toString());
      return abs.href;
    };

    const fetchAsBlob = async (u) => {
      const res = await axios.get(buildUrl(u), { responseType: "blob", withCredentials: false });
      return res.data;
    };

    const blobToPngDataUrl = (blob) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        const objUrl = URL.createObjectURL(blob);
        img.onload = () => {
          try {
            const canvas = document.createElement("canvas");
            canvas.width = img.naturalWidth || img.width || 0;
            canvas.height = img.naturalHeight || img.height || 0;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);
            const pngDataUrl = canvas.toDataURL("image/png");
            URL.revokeObjectURL(objUrl);
            resolve({ dataUrl: pngDataUrl, format: "PNG" });
          } catch (e) {
            URL.revokeObjectURL(objUrl);
            reject(e);
          }
        };
        img.onerror = (e) => { URL.revokeObjectURL(objUrl); reject(e); };
        img.src = objUrl;
      });

    try {
      const blob = await fetchAsBlob(safeUrl);
      return await blobToPngDataUrl(blob);
    } catch (directErr) {
      try {
        const proxied = toFetchable(safeUrl);
        const blob = await fetchAsBlob(proxied);
        return await blobToPngDataUrl(blob);
      } catch (proxyErr) {
        console.error("❌ Error fetching image for PDF:", proxyErr?.message || proxyErr);
        throw proxyErr;
      }
    }
  };

  // EXPORT SINGLE RECORD PDF (added otherParticulars summary)
  const exportRecordPDF = async (rec) => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const headerHeight = 20;
    doc.setFillColor(0, 102, 204);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    doc.setFontSize(16);
    doc.setTextColor(255);
    doc.text("KYC Record", 14, 14);

    const photoUrl = getCandidatePhotoUrl(rec);
    const boxX = 14;
    const boxY = headerHeight + 20;
    const boxSize = 50;

    doc.setDrawColor(0, 102, 204);
    doc.setLineWidth(1.5);
    doc.rect(boxX, boxY, boxSize, boxSize);

    const placeImage = (imgW, imgH, data, fmt, x, y) => {
      const imgX = x + (boxSize - imgW) / 2;
      const imgY = y + (boxSize - imgH) / 2;
      doc.addImage(data, fmt, imgX, imgY, imgW, imgH, undefined, "FAST");
    };

    // Candidate Photo
    if (photoUrl) {
      try {
        const { dataUrl, format } = await urlToDataURL(photoUrl);
        const tmp = new Image();
        tmp.src = dataUrl;
        await new Promise((res) => (tmp.onload = res));
        const ratio = tmp.width / tmp.height;
        let imgW, imgH;
        if (ratio > 1) {
          imgW = boxSize;
          imgH = boxSize / ratio;
        } else {
          imgH = boxSize;
          imgW = boxSize * ratio;
        }
        placeImage(imgW, imgH, dataUrl, format, boxX, boxY);
      } catch (e) {
        console.warn("PDF photo embed failed:", e?.message || e);
      }
    }

    // Aadhaar Photo (portrait)
    const aadhaarPortraitUrl = getAadhaarPortraitUrl(rec);
    const portraitBoxY = boxY + boxSize + 10; // below first box
    doc.setDrawColor(0, 102, 204);
    doc.rect(boxX, portraitBoxY, boxSize, boxSize);
    if (aadhaarPortraitUrl) {
      try {
        const { dataUrl, format } = await urlToDataURL(aadhaarPortraitUrl);
        const tmp2 = new Image();
        tmp2.src = dataUrl;
        await new Promise((res) => (tmp2.onload = res));
        const ratio2 = tmp2.width / tmp2.height;
        let w2, h2;
        if (ratio2 > 1) {
          w2 = boxSize;
          h2 = boxSize / ratio2;
        } else {
          h2 = boxSize;
          w2 = boxSize * ratio2;
        }
        placeImage(w2, h2, dataUrl, format, boxX, portraitBoxY);
      } catch (e) {
        console.warn("PDF Aadhaar portrait embed failed:", e?.message || e);
      }
    }

    // Aadhaar Document Image (uploaded file)
    const aadhaarDocUrlRaw = getAadhaarImageUrl(rec);
    const aadhaarDocUrl = aadhaarDocUrlRaw ? toFetchable(aadhaarDocUrlRaw) : null;

    const docBoxY = portraitBoxY + boxSize + 10;
    doc.setDrawColor(0, 102, 204);
    doc.rect(boxX, docBoxY, boxSize, boxSize);

    if (aadhaarDocUrl) {
      try {
        const { dataUrl, format } = await urlToDataURL(aadhaarDocUrl);
        const tmp3 = new Image();
        tmp3.src = dataUrl;
        await new Promise((res) => (tmp3.onload = res));
        const ratio3 = tmp3.width / tmp3.height;
        let w3, h3;
        if (ratio3 > 1) { w3 = boxSize; h3 = boxSize / ratio3; }
        else { h3 = boxSize; w3 = boxSize * ratio3; }
        placeImage(w3, h3, dataUrl, format, boxX, docBoxY);
      } catch (e) {
        console.warn("PDF Aadhaar document image embed failed:", e?.message || e);
      }
    }

    const tableStartX = boxX + boxSize + 10;
    const tableWidth = pageWidth - tableStartX - 14;
    const tableStartY = boxY;

    const other = rec.otherParticulars || {};

    autoTable(doc, {
      startY: tableStartY,
      margin: { left: tableStartX },
      tableWidth: tableWidth,
      theme: "grid",
      head: [["Field", "Details"]],
      body: [
        ["Name", rec.name || "-"],
        ["Mobile", rec.mobile || "-"],
        ["Aadhaar(masked)", getDigiMaskedUid(rec)],
        ["DOB", formatDate(rec.dob)],
        ["Father", rec.father || "-"],
        ["Serial No(s)", (Array.isArray(rec.serialNumbers) && rec.serialNumbers.length) ? rec.serialNumbers.join(", ") : "-"],
        ["Address", rec.address || "-"],
        ["Pincode", rec.pincode || "-"],
        ["State", rec.state || "-"],
        ["Latitude", rec.latitude || "-"],
        ["Longitude", rec.longitude || "-"],
        ["DL Status", getDigiStatus(rec)],
        ["DL Verification ID", getDigiVerificationId(rec)],
        ["Purpose(s) for licence", other.purposeDetails || "-"],
        ["Area applicant wishes to carry arms", other.carryArea || "-"],
        ["Convicted?", other.convicted || "-"],
        ["Applied before?", other.appliedBefore || "-"],
        ["License suspended?", other.licenseSuspended || "-"],
        ["Family has licence?", other.familyHasLicence || "-"],
        ["Safe place to keep?", other.safePlaceToKeep || "-"],
        ["Training under rule 10?", other.trainingUnderRule10 || "-"],
        ["Training Certificate", getTrainingCertificateUrl(rec) ? "Attached" : "-"],
        ["Additional Document", getExtraDocumentUrl(rec) ? "Attached" : "-"]
      ],
      styles: {
        fontSize: 10,
        cellPadding: 4,
        fillColor: [230, 240, 255],
        textColor: [0, 0, 0],
      },
      headStyles: {
        fillColor: [0, 102, 204],
        textColor: 255,
        fontStyle: "bold",
        halign: "center",
      },
      alternateRowStyles: { fillColor: [245, 250, 255] },
      didDrawCell: ({ section, cell }) => {
        if (section === "body") doc.roundedRect(cell.x, cell.y, cell.width, cell.height, 2, 2);
      },
    });

    const pageHeight = doc.internal.pageSize.getHeight();
    doc.setFontSize(10);
    doc.setTextColor(150);
    doc.text(`Page 1`, pageWidth / 2, pageHeight - 10, { align: "center" });

    doc.save(`kyc_${rec._id}.pdf`);
  };

  // VIEW/EDIT/DELETE
  const openView = (rec) => setViewRecord(rec);
  const closeView = () => setViewRecord(null);

  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditForm({
      name: rec.name || "",
      dob: rec.dob || "",
      father: rec.father || "",
      address: rec.address || "",
      pincode: rec.pincode || "",
      state: rec.state || "",
      serialNumbers: Array.isArray(rec.serialNumbers) ? rec.serialNumbers : [],
    });
  };
  const closeEdit = () => {
    setEditRecord(null);
    setEditForm({});
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  // serial numbers
  const handleSerialChange = (index, value) => {
    const cleaned = value.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setEditForm((prev) => {
      const updated = [...(prev.serialNumbers || [])];
      updated[index] = cleaned;
      return { ...prev, serialNumbers: updated };
    });
  };
  const addSerialNumber = () => {
    setEditForm((prev) => ( {
      ...prev,
      serialNumbers: [...(prev.serialNumbers || []), ""],
    }));
  };
  const removeSerialNumber = (index) => {
    setEditForm((prev) => {
      const updated = [...(prev.serialNumbers || [])];
      updated.splice(index, 1);
      return { ...prev, serialNumbers: updated };
    });
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    try {
      setSaving(true);
      const payload = {
        ...editForm,
        serialNumbers: Array.isArray(editForm.serialNumbers)
          ? editForm.serialNumbers
              .map((s) => String(s || "").trim().toUpperCase())
              .filter((s) => s.length > 0)
          : [],
      };

      await axios.put(`${API_BASE}/api/kyc-data/${editRecord._id}`, payload);
      await fetchData();
      closeEdit();
    } catch (err) {
      console.error("Error saving record", err);
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (id) => setDeleteId(id);
  const cancelDelete = () => setDeleteId(null);
  const doDelete = async () => {
    if (!deleteId) return;
    try {
      setDeleting(true);
      await axios.delete(`${API_BASE}/api/kyc-data/${deleteId}`);
      await fetchData();
      setDeleteId(null);
    } catch (err) {
      console.error("Error deleting record", err);
      alert("Failed to delete record");
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    document.title = "KYC Dashboard";
  }, []);

  const hasValidLatLng = (rec) => {
    const lat = Number(rec?.latitude);
    const lng = Number(rec?.longitude);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return false;
    return true;
  };

  return (
    <div 
    className="min-h-screen p-4 sm:p-8 bg-fixed bg-cover bg-center"
    style={{
      backgroundImage: "url('/MainBody03.png')",
    }}
    >

      <div className="relative z-10">
        {/* Header */}
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">
            KYC Dashboard
          </h1>
          
        <button
        onClick={handleLogout}
        className="btn-kyc btn-black min-w-[120px]"
        >
          Logout
        </button>
        </div>

        {/* Filters */}
        <div className="max-w-7xl mx-auto w-full bg-white/90 backdrop-blur-sm p-4 rounded-lg shadow-sm mb-6 grid gap-4 sm:grid-cols-5">
          <input
            type="text"
            placeholder="Search name / mobile / masked UID"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="p-2 border rounded"
          />
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="p-2 border rounded"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="p-2 border rounded"
          />
          <button
            onClick={exportCSV}
            className="btn-kyc btn-white"
          >
            Export CSV
          </button>
          <button
            onClick={exportPDF}
            className="btn-kyc btn-black"
          >
            Export PDF
          </button>
        </div>

        {/* Data Table */}
        <div className="max-w-7xl mx-auto w-full">
          {loading && <p>Loading...</p>}
          {error && <p className="text-red-600 mb-4">{error}</p>}
          {!loading && filteredData.length === 0 && !error && (
            <p className="text-gray-600">No KYC records found.</p>
          )}

          {!loading && filteredData.length > 0 && (
            <div className="hidden sm:block bg-white/95 backdrop-blur-sm rounded-lg border p-4 overflow-x-auto">
              <table className="w-full table-auto text-sm border">
                <thead>
                  <tr className="bg-gray-200 text-gray-700">
                    <th className="p-2 border">Name</th>
                    <th className="p-2 border">Mobile</th>
                    <th className="p-2 border">DOB</th>
                    <th className="p-2 border">Serial No(s)</th>
                    <th className="p-2 border">Address</th>
                    <th className="p-2 border">GPS (Lat, Lng)</th>
                    <th className="p-2 border">DigiLocker Status</th>
                    <th className="p-2 border">Aadhaar(masked)</th>
                    <th className="p-2 border">Aadhaar File</th>
                    <th className="p-2 border">Candidate Photo</th>
                    <th className="p-2 border">Aadhaar Photo</th>
                    <th className="p-2 border">Training Cert</th>
                    <th className="p-2 border">Additional Doc</th>
                    <th className="p-2 border">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredData.map((item) => (
                    <tr key={item._id} className="hover:bg-gray-50">
                      <td className="p-2 border whitespace-nowrap">
                        {item.name || "-"}
                      </td>
                      <td className="p-2 border whitespace-nowrap">
                        {item.mobile || "-"}
                      </td>
                      <td className="p-2 border whitespace-nowrap">
                        {formatDate(item.dob)}
                      </td>
                      <td className="p-2 border whitespace-nowrap">
                        {item.serialNumbers && item.serialNumbers.length > 0
                        ? item.serialNumbers.join(", ")
                        : "-"}
                      </td>
                      <td
                        className="p-2 border max-w-xs truncate"
                        title={item.address}
                      >
                        {item.address || "-"}
                      </td>

                      <td className="p-2 border whitespace-nowrap text-center">
                        {item.latitude && item.longitude ? (
                          <div>
                            <span className="text-gray-700 font-medium text-xs block">
                              {`${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}`}
                            </span>
                            <a
                              href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-blue-600 underline text-xs hover:text-blue-800"
                            >
                              <MapPinIcon size={12} className="mr-1 text-red-500" />
                              View on Map
                            </a>
                          </div>
                        ) : (
                          <span className="text-gray-400 text-xs">No location</span>
                        )}
                      </td>

                      {/* DigiLocker */}
                      <td className="p-2 border whitespace-nowrap">
                        <span className={
                          getDigiStatus(item) === "AUTHENTICATED" || getDigiStatus(item) === "SUCCESS"
                            ? "text-green-700"
                            : getDigiStatus(item) === "EXPIRED" || getDigiStatus(item) === "CONSENT_DENIED"
                            ? "text-red-600"
                            : "text-gray-700"
                        }>
                          {getDigiStatus(item)}
                        </span>
                      </td>
                      <td className="p-2 border whitespace-nowrap">{getDigiMaskedUid(item)}</td>

                      {/* Aadhaar File (image-first; fallback to static PDF icon; no link) */}
                      <td className="p-2 border whitespace-nowrap">
                        {(() => {
                          const url = getAadhaarImageUrl(item);
                          if (!url) return "-";
                          return (
                            <div className="w-10 h-10 mx-auto flex items-center justify-center">
                              <img
                                src={url}
                                alt="Aadhaar"
                                className="h-10 w-10 object-cover rounded border"
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                  const pdfThumb = e.currentTarget.nextElementSibling;
                                  if (pdfThumb) pdfThumb.style.display = "block";
                                }}
                              />
                              <img
                                src={pdfThumbnail}
                                alt="PDF file"
                                style={{ display: "none" }}
                                className="h-10 w-10 object-contain"
                                title="PDF file"
                              />
                            </div>
                          );
                        })()}
                      </td>

                      {/* Candidate Photo */}
                      <td className="p-2 border whitespace-nowrap">
                        {getCandidatePhotoUrl(item) ? (
                          <img
                            src={getCandidatePhotoUrl(item)}
                            alt="Candidate"
                            className="h-10 w-10 object-cover rounded-full border mx-auto"
                            onError={(e) => (e.currentTarget.style.display = "none")}
                          />
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Aadhaar Photo (portrait) */}
                      <td className="p-2 border whitespace-nowrap">
                        {(() => {
                          const portrait = getAadhaarPortraitUrl(item);
                          if (!portrait) return "-";
                          return (
                            <img
                              src={portrait}
                              alt="Aadhaar Photo"
                              className="h-10 w-10 object-cover rounded-full border mx-auto"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          );
                        })()}
                      </td>

                      {/* Training Certificate */}
                      <td className="p-2 border whitespace-nowrap text-center">
                        {getTrainingCertificateUrl(item) ? (
                          isImageFile(getTrainingCertificateUrl(item)) ? (
                            <img
                              src={getTrainingCertificateUrl(item)}
                              alt="Training Cert"
                              className="h-10 w-10 object-cover rounded border mx-auto"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          ) : (
                            <a
                              href={getTrainingCertificateUrl(item)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-indigo-600 underline"
                            >
                              View
                            </a>
                          )
                        ) : (
                          "-"
                        )}
                      </td>

                      {/* Additional Document */}
                      <td className="p-2 border whitespace-nowrap text-center">
                        {getExtraDocumentUrl(item) ? (
                          isImageFile(getExtraDocumentUrl(item)) ? (
                            <img
                              src={getExtraDocumentUrl(item)}
                              alt="Additional Doc"
                              className="h-10 w-10 object-cover rounded border mx-auto"
                              onError={(e) => (e.currentTarget.style.display = "none")}
                            />
                          ) : (
                            <a
                              href={getExtraDocumentUrl(item)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-indigo-600 underline"
                            >
                              View
                            </a>
                          )
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="p-2 border whitespace-nowrap">
                        <div className="flex gap-2">
                          <button
                            onClick={() => openView(item)}
                            className="px-2 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            Read More
                          </button>
                          <button
                            onClick={() => openEdit(item)}
                            className="px-2 py-1 text-xs bg-yellow-500 text-white rounded hover:bg-yellow-600"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => confirmDelete(item._id)}
                            className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-700"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="sm:hidden space-y-4">
          {filteredData.map((item) => (
            <div key={item._id} className="bg-white/95 backdrop-blur-sm p-4 rounded shadow">
              <p><span className="font-bold">Name:</span> {item.name}</p>
              <p><span className="font-bold">Mobile:</span> {item.mobile}</p>
              <p><span className="font-bold">Aadhaar(masked):</span> {getDigiMaskedUid(item)}</p>
              <p><span className="font-bold">DOB:</span> {formatDate(item.dob)}</p>
              <p>
                <span className="font-bold">Serial No(s):</span>{" "}
                {item.serialNumbers && item.serialNumbers.length > 0
                ? item.serialNumbers.join(", ")
                : "-"}
              </p>
              <p><span className="font-bold">Address:</span> {item.address}</p>
              <p><strong>GPS (Lat, Lng):</strong>{" "}
                {item.latitude && item.longitude
                  ? `${Number(item.latitude).toFixed(5)}, ${Number(item.longitude).toFixed(5)}`
                  : "N/A"}
                {item.latitude && item.longitude ? (
                  <a
                    href={`https://www.google.com/maps?q=${item.latitude},${item.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 underline text-sm ml-1 hover:text-blue-800"
                  >
                    <MapPinIcon size={14} className="mr-1 text-red-500" />
                    View on Map
                  </a>
                ) : (
                  <span className="text-gray-400 text-xs ml-1">No location</span>
                )}
              </p>

              <p><span className="font-bold">DigiLocker Status:</span> {getDigiStatus(item)}</p>

              {getCandidatePhotoUrl(item) && (
                <div className="mt-2">
                  <p className="font-bold">Candidate Photo:</p>
                  <img
                    src={getCandidatePhotoUrl(item)}
                    alt="Candidate"
                    className="w-20 h-20 object-cover rounded-full border"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                </div>
              )}

              {getAadhaarImageUrl(item) && (
                <div className="mt-2">
                  <p className="font-bold">Aadhaar File:</p>
                  {isImageFile(getAadhaarImageUrl(item)) ? (
                    <img
                      src={getAadhaarImageUrl(item)}
                      alt="Aadhaar"
                      className="w-20 h-20 object-cover rounded border"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  ) : (
                    <a
                      href={getAadhaarImageUrl(item)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 underline"
                    >
                      View / Download
                    </a>
                  )}
                </div>
              )}

              {(() => {
                const portrait = getAadhaarPortraitUrl(item);
                if (!portrait) return null;
                return (
                  <div className="mt-2">
                    <p className="font-bold">Aadhaar Photo:</p>
                    <img
                      src={portrait}
                      alt="Aadhaar Photo"
                      className="w-20 h-20 object-cover rounded-full border"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                );
              })()}

              {getTrainingCertificateUrl(item) && (
                <div className="mt-2">
                  <p className="font-bold">Training Certificate:</p>
                  {isImageFile(getTrainingCertificateUrl(item)) ? (
                    <img src={getTrainingCertificateUrl(item)} alt="Training" className="w-20 h-20 object-cover rounded border" onError={(e)=> (e.currentTarget.style.display='none')} />
                  ) : (
                    <a href={getTrainingCertificateUrl(item)} target="_blank" rel="noreferrer" className="text-indigo-600 underline">View</a>
                  )}
                </div>
              )}

              {getExtraDocumentUrl(item) && (
                <div className="mt-2">
                  <p className="font-bold">Additional Document:</p>
                  {isImageFile(getExtraDocumentUrl(item)) ? (
                    <img src={getExtraDocumentUrl(item)} alt="Extra" className="w-20 h-20 object-cover rounded border" onError={(e)=> (e.currentTarget.style.display='none')} />
                  ) : (
                    <a href={getExtraDocumentUrl(item)} target="_blank" rel="noreferrer" className="text-indigo-600 underline">View</a>
                  )}
                </div>
              )}

              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => openView(item)}
                  className="px-2 py-1 text-xs bg-gray-900 text-white rounded hover:bg-black"
                >
                  Read More
                </button>
                <button
                  onClick={() => openEdit(item)}
                  className="px-2 py-1 text-xs bg-gray-600 text-white rounded hover:bg-gray-700"
                >
                  Edit
                </button>
                <button
                  onClick={() => confirmDelete(item._id)}
                  className="px-2 py-1 text-xs bg-gray-300 text-gray-900 rounded hover:bg-gray-400"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* ---------------- READ MORE MODAL ---------------- */}
        {viewRecord && (
          <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
            <div className="bg-white/95 backdrop-blur-sm w-full max-w-lg rounded-lg shadow-lg p-6 relative max-h-screen overflow-y-auto">
              <div
                className="absolute inset-0 flex items-center justify-center pointer-events-none"
                style={{
                  backgroundImage: "url('/advitiya_trans.png')",
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "200px auto",
                  opacity: 0.1,
                }}
              />
              <button
                onClick={closeView}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
              <h2 className="text-xl font-bold text-gray-900">KYC Details</h2>

              {/* Candidate Photo */}
              {getCandidatePhotoUrl(viewRecord) && (
                <div className="mb-4 text-center">
                  <img
                    src={getCandidatePhotoUrl(viewRecord)}
                    alt="Candidate"
                    className="w-32 h-32 object-cover rounded-full border mx-auto"
                    onError={(e) => (e.currentTarget.style.display = "none")}
                  />
                  <p className="text-sm text-gray-600 mt-1">Candidate Photo</p>
                </div>
              )}

              {/* Table View */}
              <table className="w-full text-sm border border-gray-200 rounded mb-4">
                <tbody>
                  <TableRow label="Name" value={viewRecord.name} />
                  <TableRow label="Mobile" value={viewRecord.mobile} />
                  <TableRow label="Aadhaar(masked)" value={getDigiMaskedUid(viewRecord)} />
                  <TableRow label="Serial Number(s)"
                    value={
                      viewRecord.serialNumbers && viewRecord.serialNumbers.length > 0
                      ? viewRecord.serialNumbers.join(", ")
                      : "-"
                    }
                  />
                  <TableRow label="DOB" value={formatDate(viewRecord.dob)} />
                  <TableRow label="Father" value={viewRecord.father} />
                  <TableRow label="Address" value={viewRecord.address} />
                  <TableRow label="Pincode" value={viewRecord.pincode} />
                  <TableRow label="State" value={viewRecord.state} />

                  {/* GPS */}
                  <tr className="border-b border-gray-200">
                    <td className="p-2 font-medium text-gray-700 bg-gray-50">GPS (Lat, Lng)</td>
                    <td className="p-2 text-gray-800">
                      {viewRecord.latitude && viewRecord.longitude
                        ? `${Number(viewRecord.latitude).toFixed(5)}, ${Number(viewRecord.longitude).toFixed(5)}`
                        : "N/A"}
                      {viewRecord.latitude && viewRecord.longitude ? (
                        <a
                          href={`https://www.google.com/maps?q=${viewRecord.latitude},${viewRecord.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center ml-2 text-blue-600 underline text-xs hover:text-blue-800"
                        >
                          <MapPinIcon size={12} className="mr-1 text-red-500" />
                          Open in Google Maps
                        </a>
                      ) : (
                        <span className="text-gray-400 text-xs ml-1">No location</span>
                      )}
                    </td>
                  </tr>

                  <TableRow label="DigiLocker Status" value={getDigiStatus(viewRecord)} />
                  <TableRow label="DL Verification ID" value={getDigiVerificationId(viewRecord)} />
                </tbody>
              </table>

              {/* ===== Leaflet Map (only if coords are valid) ===== */}
              {hasValidLatLng(viewRecord) && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-600 mb-2">
                    Location Preview
                  </p>
                  <div className="rounded border overflow-hidden" style={{ height: 260 }}>
                    <MapContainer
                      center={[Number(viewRecord.latitude), Number(viewRecord.longitude)]}
                      zoom={16}
                      style={{ height: "100%", width: "100%" }}
                      scrollWheelZoom
                    >
                      <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution="&copy; OpenStreetMap contributors"
                      />
                      <Marker
                        position={[Number(viewRecord.latitude), Number(viewRecord.longitude)]}
                      />
                    </MapContainer>
                  </div>
                </div>
              )}
              {/* ===== end Leaflet Map ===== */}

              {/* Aadhaar Document + Aadhaar Photo side-by-side */}
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Aadhaar Document Preview */}
                {(() => {
                  const url = getAadhaarImageUrl(viewRecord);
                  if (!url) return null;

                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Aadhaar File</p>
                      <img
                        src={url}
                        alt="Aadhaar"
                        className="max-h-48 w-auto rounded border"
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const pdfThumb = e.currentTarget.nextElementSibling;
                          if (pdfThumb) pdfThumb.style.display = "block";
                        }}
                      />
                      <img
                        src={pdfThumbnail}
                        alt="PDF file"
                        style={{ display: "none" }}
                        className="h-16 w-16 object-contain mt-2"
                        title="PDF file"
                      />
                      <div className="mt-2">
                        <a href={url} target="_blank" rel="noreferrer" className="text-indigo-600 underline text-sm">Open Document</a>
                      </div>
                    </div>
                  );
                })()}

                {/* Aadhaar Photo (portrait from digilocker/snapshot) */}
                {(() => {
                  const portrait = getAadhaarPortraitUrl(viewRecord);
                  if (!portrait) return null;
                  return (
                    <div>
                      <p className="text-xs font-semibold text-gray-600 mb-1">Aadhaar Photo</p>
                      <img
                        src={portrait}
                        alt="Aadhaar Photo"
                        className="max-h-48 w-auto rounded border object-cover"
                        onError={(e) => { e.currentTarget.style.display = "none"; }}
                      />
                    </div>
                  );
                })()}
              </div>

              {/* Other particulars */}
              <div className="mt-6">
                <h3 className="font-semibold mb-2">Other particulars</h3>
                {viewRecord.otherParticulars ? (
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    {[
                      ["Convicted", "convicted"],
                      ["Applied before", "appliedBefore"],
                      ["License suspended/cancelled/revoked", "licenseSuspended"],
                      ["Family has licence", "familyHasLicence"],
                      ["Safe place to keep arms", "safePlaceToKeep"],
                      ["Training under rule 10", "trainingUnderRule10"],
                      ["Purpose(s)", "purposeDetails"],
                      ["Area applicant wishes to carry arms", "carryArea"]
                    ].map(([label, key]) => (
                      <div key={key} className="flex gap-2">
                        <div className="w-48 text-gray-600">{label}</div>
                        <div className="font-medium text-gray-800">
                          {String(viewRecord.otherParticulars[key] ?? "-")}
                        </div>
                      </div>
                    ))}

                    {/* Training Certificate link */}
                    <div className="flex gap-2">
                      <div className="w-48 text-gray-600">Training Certificate</div>
                      <div className="font-medium text-gray-800">
                        {getTrainingCertificateUrl(viewRecord) ? (
                          isImageFile(getTrainingCertificateUrl(viewRecord)) ? (
                            <a href={getTrainingCertificateUrl(viewRecord)} target="_blank" rel="noreferrer" className="underline text-indigo-600">Open Image</a>
                          ) : (
                            <a href={getTrainingCertificateUrl(viewRecord)} target="_blank" rel="noreferrer" className="underline text-indigo-600">Open Document</a>
                          )
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>

                    {/* Additional Document link */}
                    <div className="flex gap-2">
                      <div className="w-48 text-gray-600">Additional Document</div>
                      <div className="font-medium text-gray-800">
                        {getExtraDocumentUrl(viewRecord) ? (
                          isImageFile(getExtraDocumentUrl(viewRecord)) ? (
                            <a href={getExtraDocumentUrl(viewRecord)} target="_blank" rel="noreferrer" className="underline text-indigo-600">Open Image</a>
                          ) : (
                            <a href={getExtraDocumentUrl(viewRecord)} target="_blank" rel="noreferrer" className="underline text-indigo-600">Open Document</a>
                          )
                        ) : (
                          "-"
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-600">No additional particulars recorded.</div>
                )}
              </div>

              {/* Buttons */}
              <div className="mt-6 flex flex-wrap justify-end gap-2">
                <button
                  onClick={() => exportRecordPDF(viewRecord)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded"
                >
                  Download PDF
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- EDIT MODAL ---------------- */}
        {editRecord && (
          <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
            <div className="bg-white/95 backdrop-blur-sm w-full max-w-lg rounded-lg shadow-lg p-6 relative">
              <button
                onClick={closeEdit}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
              <h2 className="text-xl font-bold text-purple-700 mb-4">
                Edit KYC Record
              </h2>

              <div className="space-y-3">
                <EditField
                  label="Name"
                  name="name"
                  value={editForm.name}
                  onChange={handleEditChange}
                />
                <EditField
                  label="DOB"
                  name="dob"
                  type="date"
                  value={editForm.dob}
                  onChange={handleEditChange}
                />
                <EditField
                  label="Father"
                  name="father"
                  value={editForm.father}
                  onChange={handleEditChange}
                />
                <EditField
                  label="Address"
                  name="address"
                  value={editForm.address}
                  onChange={handleEditChange}
                />
                <EditField
                  label="Pincode"
                  name="pincode"
                  value={editForm.pincode}
                  onChange={handleEditChange}
                />
                <EditField
                  label="State"
                  name="state"
                  value={editForm.state}
                  onChange={handleEditChange}
                />
              </div>

              {/* SERIAL NUMBERS */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-2">
                  Serial Number(s)
                </label>

                {(editForm.serialNumbers || []).map((sn, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={sn}
                      onChange={(e) => handleSerialChange(idx, e.target.value)}
                      placeholder="Enter alphanumeric serial"
                      className="flex-1 p-2 border rounded uppercase"
                    />
                    <button
                      type="button"
                      onClick={() => removeSerialNumber(idx)}
                      className="text-red-600 text-xs font-semibold hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <button
                  type="button"
                  onClick={addSerialNumber}
                  className="text-blue-600 text-sm font-semibold hover:underline mt-1"
                >
                  + Add Serial Number
                </button>

                <p className="text-xs text-gray-500 mt-1">
                  Only A–Z and 0–9 allowed. Example: ABC123 or X9T7Z.
                </p>
              </div>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={closeEdit}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ---------------- DELETE MODAL ---------------- */}
        {deleteId && (
          <div className="fixed inset-0 bg-black/50 z-40 flex items-center justify-center p-4">
            <div className="bg-white/95 backdrop-blur-sm w-full max-w-sm rounded-lg shadow-lg p-6 text-center relative">
              <button
                onClick={cancelDelete}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
              <h2 className="text-lg font-bold text-red-600 mb-2">
                Confirm Delete
              </h2>
              <p className="text-gray-700 mb-4">
                Are you sure you want to delete this record?
              </p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={cancelDelete}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={doDelete}
                  disabled={deleting}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded"
                >
                  {deleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const TableRow = ({ label, value }) => (
  <tr className="border-b border-gray-200">
    <td className="p-2 font-medium text-gray-700 bg-gray-50">{label}</td>
    <td className="p-2 text-gray-800">{value || "-"}</td>
  </tr>
);

const EditField = ({ label, name, value, onChange, type = "text" }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      className="w-full p-2 border rounded"
    />
  </div>
);

export default Dashboard;
